import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  TextField,
  Card,
  CardContent,
  Stack,
  IconButton,
  Avatar,
  Paper,
  Divider,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from '@mui/material';
import {
  LocationOn as LocationIcon,
  Instagram as InstagramIcon,
  Twitter as TwitterIcon,
  Facebook as FacebookIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Nfc as NfcIcon,
  OpenInNew as OpenInNewIcon,
  ContentCopy as CopyIcon,
  Payment as PaymentIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import SEO from '../../components/SEO';

// Custom Discord SVG Icon
const DiscordIcon = (props: any) => (
  <svg
    viewBox="0 0 127.14 96.36"
    width="20"
    height="20"
    fill="currentColor"
    style={{ display: 'inline-block', verticalAlign: 'middle' }}
    {...props}
  >
    <path d="M107.7,8.07A105.15,105.15,0,0,0,77.26,0a77.19,77.19,0,0,0-3.3,6.83A96.67,96.67,0,0,0,53.22,6.83,77.19,77.19,0,0,0,49.88,0,105.15,105.15,0,0,0,19.44,8.07C3.66,31.58-1.86,54.65,1,77.53A105.73,105.73,0,0,0,32,96.36a77.7,77.7,0,0,0,6.63-10.85,68.43,68.43,0,0,1-10.45-5c.87-.64,1.72-1.31,2.53-2a75.76,75.76,0,0,0,72.82,0c.81.69,1.66,1.36,2.53,2a68.86,68.86,0,0,1-10.45,5,78.29,78.29,0,0,0,6.63,10.85,105.73,105.73,0,0,0,31.58-18.83C129,54.65,122.64,31.58,107.7,8.07ZM42.45,65.69C36.18,65.69,31,60,31,53S36.18,40.36,42.45,40.36,53.83,46,53.83,53,48.72,65.69,42.45,65.69Zm42.24,0C78.41,65.69,73.24,60,73.24,53S78.41,40.36,84.69,40.36,96.07,46,96.07,53,91,65.69,84.69,65.69Z" />
  </svg>
);

// Custom Zelle Icon
const ZelleIcon = (props: any) => (
  <Typography
    variant="button"
    sx={{
      fontWeight: 'bold',
      fontStyle: 'italic',
      fontSize: '1rem',
      lineHeight: 1,
      fontFamily: 'sans-serif',
      color: 'white',
      ...props.sx,
    }}
  >
    zelle
  </Typography>
);

interface StoreProfile {
  storeName: string;
  bio: string;
  address: string;
  venmo: string;
  zelle: string;
  instagram: string;
  discord: string;
  twitter: string;
  facebook: string;
}

const defaultProfile: StoreProfile = {
  storeName: 'Chasing TCG Local Store',
  bio: 'Your local hub for Pokémon & One Piece trading cards. Mon - Sat: 11 AM - 7 PM.',
  address: '888 Card Vault Way, Suite 100, Los Angeles, CA 90017',
  venmo: 'chasingtcg-store',
  zelle: 'pay@chasingtcg.com',
  instagram: 'chasingtcg_la',
  discord: 'chasingtcg-community',
  twitter: 'chasingtcg_store',
  facebook: 'chasingtcgstore',
};

const NfcSharePage: React.FC = () => {
  const { storeId = 'default' } = useParams<{ storeId?: string }>();
  const { isAuthenticated, user } = useAuth();
  
  // Profile State
  const [profile, setProfile] = useState<StoreProfile>(defaultProfile);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editedProfile, setEditedProfile] = useState<StoreProfile>(defaultProfile);

  // UI State
  const [toastMessage, setToastMessage] = useState('');
  const [toastSeverity, setToastSeverity] = useState<'success' | 'error' | 'info'>('success');
  const [toastOpen, setToastOpen] = useState(false);

  // NFC Programming State
  const [nfcWriteOpen, setNfcWriteOpen] = useState(false);
  const [nfcWriteStatus, setNfcWriteStatus] = useState<'idle' | 'writing' | 'success' | 'error'>('idle');
  const [nfcErrorDetails, setNfcErrorDetails] = useState('');

  // Load from local storage on mount/storeId change
  useEffect(() => {
    const saved = localStorage.getItem(`store-nfc-profile-${storeId}`);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setProfile(parsed);
        setEditedProfile(parsed);
      } catch (e) {
        console.error('Failed to parse saved store NFC profile', e);
      }
    } else {
      setProfile(defaultProfile);
      setEditedProfile(defaultProfile);
    }
  }, [storeId]);

  const showToast = (message: string, severity: 'success' | 'error' | 'info' = 'success') => {
    setToastMessage(message);
    setToastSeverity(severity);
    setToastOpen(true);
  };

  // Maps Navigation URL
  const getMapsUrl = (addressStr: string) => {
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(addressStr)}`;
  };

  const handleNavigate = () => {
    if (!profile.address) {
      showToast('Store address is not set yet.', 'error');
      return;
    }
    window.open(getMapsUrl(profile.address), '_blank');
  };

  // Copy Zelle detail to clipboard
  const handleCopyZelle = () => {
    if (!profile.zelle) return;
    navigator.clipboard.writeText(profile.zelle);
    showToast(`Zelle account copied to clipboard: ${profile.zelle}`, 'success');
  };

  // Edit Mode actions
  const handleToggleEdit = () => {
    if (!isEditMode) {
      setEditedProfile({ ...profile });
    }
    setIsEditMode(!isEditMode);
  };

  const handleInputChange = (field: keyof StoreProfile, value: string) => {
    setEditedProfile((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSaveProfile = () => {
    if (!editedProfile.storeName.trim()) {
      showToast('Store Name is required.', 'error');
      return;
    }
    setProfile(editedProfile);
    localStorage.setItem(`store-nfc-profile-${storeId}`, JSON.stringify(editedProfile));
    setIsEditMode(false);
    showToast('Store details updated successfully!', 'success');
  };

  // ----------------------------------------------------
  // PHYSICAL NFC TAG WRITER FLOW
  // ----------------------------------------------------
  const handleWriteNfcCard = async () => {
    if (!('NDEFReader' in window)) {
      setNfcWriteStatus('error');
      setNfcErrorDetails(
        'Web NFC is not supported on this browser. Try Chrome on Android. For iOS/Safari, write this URL manually using NFC Tools: ' +
          window.location.href
      );
      setNfcWriteOpen(true);
      return;
    }

    setNfcWriteOpen(true);
    setNfcWriteStatus('writing');
    
    try {
      // @ts-ignore (NDEFReader is experimental type-wise)
      const ndef = new NDEFReader();
      await ndef.write({
        records: [{ recordType: "url", data: window.location.href }]
      });
      setNfcWriteStatus('success');
      showToast('NFC Store Card Programmed successfully!', 'success');
    } catch (err: any) {
      console.error('NFC Write Error:', err);
      setNfcWriteStatus('error');
      setNfcErrorDetails(err?.message || 'Permission denied or NFC sensor disconnected.');
    }
  };

  return (
    <>
      <SEO
        title={`${profile.storeName} - Social Hub`}
        description={`Tap to follow ${profile.storeName}. Location map, checkout details (Venmo, Zelle), and social networks.`}
      />
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'flex-start',
          py: 4,
          px: 2,
          background: 'linear-gradient(135deg, #090616 0%, #110928 50%, #030206 100%)',
          color: 'white',
        }}
      >
        <Container maxWidth="xs" sx={{ p: 0 }}>
          {/* Header Controls (Edit Mode / NFC Tag Writer) */}
          <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
            <Typography variant="caption" sx={{ letterSpacing: '0.1em', opacity: 0.6, fontWeight: 'bold', textTransform: 'uppercase' }}>
              Store NFC Card
            </Typography>
            <Box display="flex" gap={1}>
              {/* NFC Program Button */}
              <Button
                variant="outlined"
                color="secondary"
                size="small"
                startIcon={<NfcIcon />}
                onClick={handleWriteNfcCard}
                sx={{
                  borderRadius: 2,
                  borderColor: 'secondary.main',
                  textTransform: 'none',
                  fontSize: '0.75rem',
                  px: 1.5,
                  bgcolor: 'rgba(156, 39, 176, 0.05)',
                }}
              >
                Program Card
              </Button>
              {/* Admin Edit Controls */}
              <IconButton
                size="small"
                onClick={handleToggleEdit}
                sx={{
                  bgcolor: 'rgba(255, 255, 255, 0.05)',
                  color: isEditMode ? 'primary.light' : 'white',
                  '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.1)' },
                }}
              >
                {isEditMode ? <ArrowBackIcon /> : <EditIcon />}
              </IconButton>
            </Box>
          </Box>

          {isEditMode ? (
            // ==========================================
            // EDIT STORE PROFILE MODE
            // ==========================================
            <Paper
              variant="outlined"
              sx={{
                p: 3,
                borderRadius: 4,
                bgcolor: 'rgba(255, 255, 255, 0.03)',
                borderColor: 'rgba(255, 255, 255, 0.07)',
                backdropFilter: 'blur(20px)',
                color: 'white',
              }}
            >
              <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, color: 'primary.light' }}>
                Configure Store Details
              </Typography>
              <Stack spacing={2}>
                <TextField
                  fullWidth
                  label="Store Name"
                  variant="outlined"
                  size="small"
                  value={editedProfile.storeName}
                  onChange={(e) => handleInputChange('storeName', e.target.value)}
                  InputLabelProps={{ style: { color: 'rgba(255, 255, 255, 0.7)' } }}
                  inputProps={{ style: { color: 'white' } }}
                  sx={{ '& .MuiOutlinedInput-root': { '& fieldset': { borderColor: 'rgba(255,255,255,0.2)' } } }}
                />
                <TextField
                  fullWidth
                  label="Bio / Hours"
                  variant="outlined"
                  size="small"
                  multiline
                  rows={2}
                  value={editedProfile.bio}
                  onChange={(e) => handleInputChange('bio', e.target.value)}
                  InputLabelProps={{ style: { color: 'rgba(255, 255, 255, 0.7)' } }}
                  inputProps={{ style: { color: 'white' } }}
                  sx={{ '& .MuiOutlinedInput-root': { '& fieldset': { borderColor: 'rgba(255,255,255,0.2)' } } }}
                />
                <TextField
                  fullWidth
                  label="Physical Address"
                  variant="outlined"
                  size="small"
                  multiline
                  rows={2}
                  value={editedProfile.address}
                  onChange={(e) => handleInputChange('address', e.target.value)}
                  placeholder="Clickable map address"
                  InputLabelProps={{ style: { color: 'rgba(255, 255, 255, 0.7)' } }}
                  inputProps={{ style: { color: 'white' } }}
                  sx={{ '& .MuiOutlinedInput-root': { '& fieldset': { borderColor: 'rgba(255,255,255,0.2)' } } }}
                />
                <Divider sx={{ my: 1, borderColor: 'rgba(255,255,255,0.1)' }} />
                <Typography variant="subtitle2" sx={{ color: 'secondary.light', fontWeight: 'bold' }}>
                  Checkout & Payment Accounts
                </Typography>
                <TextField
                  fullWidth
                  label="Venmo @Username"
                  variant="outlined"
                  size="small"
                  value={editedProfile.venmo}
                  onChange={(e) => handleInputChange('venmo', e.target.value)}
                  placeholder="e.g. megatcg"
                  InputLabelProps={{ style: { color: 'rgba(255, 255, 255, 0.7)' } }}
                  inputProps={{ style: { color: 'white' } }}
                  sx={{ '& .MuiOutlinedInput-root': { '& fieldset': { borderColor: 'rgba(255,255,255,0.2)' } } }}
                />
                <TextField
                  fullWidth
                  label="Zelle Email or Phone"
                  variant="outlined"
                  size="small"
                  value={editedProfile.zelle}
                  onChange={(e) => handleInputChange('zelle', e.target.value)}
                  placeholder="e.g. pay@megatcg.com"
                  InputLabelProps={{ style: { color: 'rgba(255, 255, 255, 0.7)' } }}
                  inputProps={{ style: { color: 'white' } }}
                  sx={{ '& .MuiOutlinedInput-root': { '& fieldset': { borderColor: 'rgba(255,255,255,0.2)' } } }}
                />
                <Divider sx={{ my: 1, borderColor: 'rgba(255,255,255,0.1)' }} />
                <Typography variant="subtitle2" sx={{ color: 'secondary.light', fontWeight: 'bold' }}>
                  Social Handles (No '@' or URL)
                </Typography>
                <TextField
                  fullWidth
                  label="Instagram Username"
                  variant="outlined"
                  size="small"
                  value={editedProfile.instagram}
                  onChange={(e) => handleInputChange('instagram', e.target.value)}
                  InputLabelProps={{ style: { color: 'rgba(255, 255, 255, 0.7)' } }}
                  inputProps={{ style: { color: 'white' } }}
                  sx={{ '& .MuiOutlinedInput-root': { '& fieldset': { borderColor: 'rgba(255,255,255,0.2)' } } }}
                />
                <TextField
                  fullWidth
                  label="Discord Invite Code / Handle"
                  variant="outlined"
                  size="small"
                  value={editedProfile.discord}
                  onChange={(e) => handleInputChange('discord', e.target.value)}
                  placeholder="Invite code or username"
                  InputLabelProps={{ style: { color: 'rgba(255, 255, 255, 0.7)' } }}
                  inputProps={{ style: { color: 'white' } }}
                  sx={{ '& .MuiOutlinedInput-root': { '& fieldset': { borderColor: 'rgba(255,255,255,0.2)' } } }}
                />
                <TextField
                  fullWidth
                  label="X / Twitter Handle"
                  variant="outlined"
                  size="small"
                  value={editedProfile.twitter}
                  onChange={(e) => handleInputChange('twitter', e.target.value)}
                  InputLabelProps={{ style: { color: 'rgba(255, 255, 255, 0.7)' } }}
                  inputProps={{ style: { color: 'white' } }}
                  sx={{ '& .MuiOutlinedInput-root': { '& fieldset': { borderColor: 'rgba(255,255,255,0.2)' } } }}
                />
                <TextField
                  fullWidth
                  label="Facebook Page Slug"
                  variant="outlined"
                  size="small"
                  value={editedProfile.facebook}
                  onChange={(e) => handleInputChange('facebook', e.target.value)}
                  InputLabelProps={{ style: { color: 'rgba(255, 255, 255, 0.7)' } }}
                  inputProps={{ style: { color: 'white' } }}
                  sx={{ '& .MuiOutlinedInput-root': { '& fieldset': { borderColor: 'rgba(255,255,255,0.2)' } } }}
                />
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<SaveIcon />}
                  onClick={handleSaveProfile}
                  sx={{
                    mt: 2,
                    borderRadius: 2,
                    fontWeight: 'bold',
                    py: 1.2,
                    background: 'linear-gradient(45deg, #3f51b5 30%, #2196f3 90%)',
                  }}
                >
                  Save Store Details
                </Button>
              </Stack>
            </Paper>
          ) : (
            // ==========================================
            // PREVIEW / ACTIVE MOBILE LANDING MODE
            // ==========================================
            <Stack spacing={2.5}>
              {/* Store Avatar Card */}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  textAlign: 'center',
                  py: 3,
                  px: 2,
                  borderRadius: 4,
                  bgcolor: 'rgba(255, 255, 255, 0.03)',
                  border: '1px solid rgba(255, 255, 255, 0.06)',
                  backdropFilter: 'blur(20px)',
                  boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.3)',
                }}
              >
                <Avatar
                  sx={{
                    width: 72,
                    height: 72,
                    bgcolor: 'primary.main',
                    mb: 2,
                    border: '3px solid rgba(255, 255, 255, 0.2)',
                    boxShadow: '0 4px 10px rgba(0, 0, 0, 0.4)',
                    fontSize: '2rem',
                    fontWeight: 'bold',
                  }}
                >
                  {profile.storeName.charAt(0).toUpperCase()}
                </Avatar>
                <Typography variant="h5" sx={{ fontWeight: 900, mb: 1, letterSpacing: '-0.01em' }}>
                  {profile.storeName}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.8, maxWidth: '270px', mx: 'auto', lineHeight: 1.5 }}>
                  {profile.bio}
                </Typography>
              </Box>

              {/* Clickable Maps Address Navigation Card */}
              {profile.address && (
                <Card
                  onClick={handleNavigate}
                  sx={{
                    cursor: 'pointer',
                    borderRadius: 4,
                    bgcolor: 'rgba(255, 255, 255, 0.04)',
                    border: '1px solid rgba(46, 125, 50, 0.2)',
                    boxShadow: '0 4px 15px rgba(0, 0, 0, 0.2)',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      borderColor: 'rgba(76, 175, 80, 0.5)',
                      bgcolor: 'rgba(255, 255, 255, 0.06)',
                      boxShadow: '0 8px 25px rgba(76, 175, 80, 0.15)',
                    },
                    '&:active': {
                      transform: 'scale(0.98)',
                    },
                  }}
                >
                  <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, p: '16px !important' }}>
                    <Box
                      sx={{
                        p: 1.2,
                        borderRadius: 3,
                        bgcolor: 'rgba(76, 175, 80, 0.1)',
                        color: 'success.light',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <LocationIcon sx={{ fontSize: 26 }} />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: 'success.light', mb: 0.25 }}>
                        Find Us / Navigate Here
                      </Typography>
                      <Typography variant="body2" noWrap sx={{ opacity: 0.8, pr: 1, fontSize: '0.825rem' }}>
                        {profile.address}
                      </Typography>
                    </Box>
                    <IconButton size="small" color="success">
                      <OpenInNewIcon fontSize="small" />
                    </IconButton>
                  </CardContent>
                </Card>
              )}

              {/* checkout & payments header */}
              {(profile.venmo || profile.zelle) && (
                <Box>
                  <Typography variant="caption" sx={{ letterSpacing: '0.05em', opacity: 0.5, fontWeight: 'bold', textTransform: 'uppercase', px: 1 }}>
                    Store Checkout / Payments
                  </Typography>
                  <Stack spacing={1.5} sx={{ mt: 1 }}>
                    {/* Venmo Button */}
                    {profile.venmo && (
                      <Button
                        variant="contained"
                        fullWidth
                        startIcon={<PaymentIcon />}
                        onClick={() => window.open(`https://venmo.com/u/${profile.venmo}`, '_blank')}
                        sx={{
                          py: 1.6,
                          borderRadius: 3,
                          fontWeight: 'bold',
                          textTransform: 'none',
                          fontSize: '1rem',
                          background: 'linear-gradient(45deg, #008CFF 30%, #00CFFF 90%)',
                          boxShadow: '0 4px 15px rgba(0, 140, 255, 0.2)',
                          justifyContent: 'flex-start',
                          px: 3,
                          '&:hover': {
                            transform: 'translateY(-1px)',
                            boxShadow: '0 6px 20px rgba(0, 140, 255, 0.3)',
                          },
                        }}
                      >
                        Pay Store via Venmo (@{profile.venmo})
                      </Button>
                    )}

                    {/* Zelle Widget */}
                    {profile.zelle && (
                      <Button
                        variant="contained"
                        fullWidth
                        startIcon={<ZelleIcon />}
                        onClick={handleCopyZelle}
                        sx={{
                          py: 1.6,
                          borderRadius: 3,
                          fontWeight: 'bold',
                          textTransform: 'none',
                          fontSize: '1rem',
                          bgcolor: '#6b2d5c',
                          boxShadow: '0 4px 15px rgba(107, 45, 92, 0.2)',
                          justifyContent: 'flex-start',
                          px: 3,
                          '&:hover': {
                            bgcolor: '#7b1fa2',
                            transform: 'translateY(-1px)',
                            boxShadow: '0 6px 20px rgba(107, 45, 92, 0.3)',
                          },
                        }}
                      >
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                          <Typography variant="inherit">
                            Pay Store via Zelle
                          </Typography>
                          <Stack direction="row" spacing={0.5} alignItems="center" sx={{ opacity: 0.8 }}>
                            <Typography variant="caption" sx={{ fontSize: '0.65rem', border: '1px solid white', borderRadius: 1, px: 0.5 }}>
                              COPY
                            </Typography>
                          </Stack>
                        </Box>
                      </Button>
                    )}
                  </Stack>
                </Box>
              )}

              {/* social platforms */}
              <Box>
                <Typography variant="caption" sx={{ letterSpacing: '0.05em', opacity: 0.5, fontWeight: 'bold', textTransform: 'uppercase', px: 1 }}>
                  Follow Store Communities
                </Typography>
                <Stack spacing={1.5} sx={{ mt: 1 }}>
                  {/* Instagram */}
                  {profile.instagram && (
                    <Button
                      variant="contained"
                      fullWidth
                      startIcon={<InstagramIcon />}
                      onClick={() => window.open(`https://instagram.com/${profile.instagram}`, '_blank')}
                      sx={{
                        py: 1.5,
                        borderRadius: 3,
                        fontWeight: 'bold',
                        textTransform: 'none',
                        fontSize: '0.95rem',
                        background: 'linear-gradient(45deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%)',
                        boxShadow: '0 4px 15px rgba(220, 39, 67, 0.2)',
                        justifyContent: 'flex-start',
                        px: 3,
                        '&:hover': {
                          transform: 'translateY(-1px)',
                        },
                      }}
                    >
                      Follow Store Instagram
                    </Button>
                  )}

                  {/* Discord */}
                  {profile.discord && (
                    <Button
                      variant="contained"
                      fullWidth
                      startIcon={<DiscordIcon />}
                      onClick={() => {
                        const link = profile.discord.includes('http') ? profile.discord : `https://discord.gg/${profile.discord}`;
                        window.open(link, '_blank');
                      }}
                      sx={{
                        py: 1.5,
                        borderRadius: 3,
                        fontWeight: 'bold',
                        textTransform: 'none',
                        fontSize: '0.95rem',
                        bgcolor: '#5865F2',
                        boxShadow: '0 4px 15px rgba(88, 101, 242, 0.2)',
                        justifyContent: 'flex-start',
                        px: 3,
                        '&:hover': {
                          bgcolor: '#4752c4',
                          transform: 'translateY(-1px)',
                        },
                      }}
                    >
                      Join Store Discord Server
                    </Button>
                  )}

                  {/* Twitter / X */}
                  {profile.twitter && (
                    <Button
                      variant="contained"
                      fullWidth
                      startIcon={<TwitterIcon />}
                      onClick={() => window.open(`https://twitter.com/${profile.twitter}`, '_blank')}
                      sx={{
                        py: 1.5,
                        borderRadius: 3,
                        fontWeight: 'bold',
                        textTransform: 'none',
                        fontSize: '0.95rem',
                        bgcolor: '#0f1419',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        boxShadow: '0 4px 15px rgba(0, 0, 0, 0.3)',
                        justifyContent: 'flex-start',
                        px: 3,
                        '&:hover': {
                          bgcolor: '#1a1f26',
                          transform: 'translateY(-1px)',
                        },
                      }}
                    >
                      Store Twitter / X
                    </Button>
                  )}

                  {/* Facebook */}
                  {profile.facebook && (
                    <Button
                      variant="contained"
                      fullWidth
                      startIcon={<FacebookIcon />}
                      onClick={() => window.open(`https://facebook.com/${profile.facebook}`, '_blank')}
                      sx={{
                        py: 1.5,
                        borderRadius: 3,
                        fontWeight: 'bold',
                        textTransform: 'none',
                        fontSize: '0.95rem',
                        bgcolor: '#1877F2',
                        boxShadow: '0 4px 15px rgba(24, 119, 242, 0.2)',
                        justifyContent: 'flex-start',
                        px: 3,
                        '&:hover': {
                          bgcolor: '#166fe5',
                          transform: 'translateY(-1px)',
                        },
                      }}
                    >
                      Follow Store Facebook Page
                    </Button>
                  )}
                </Stack>
              </Box>
            </Stack>
          )}

          {/* Footer Logo */}
          <Box sx={{ mt: 6, mb: 2, textAlign: 'center', opacity: 0.4 }}>
            <Typography variant="caption" sx={{ fontWeight: 'bold', letterSpacing: '0.15em', textTransform: 'uppercase' }}>
              Chasing TCG Ecosystem
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* NFC Program Dialog */}
      <Dialog open={nfcWriteOpen} onClose={() => setNfcWriteOpen(false)} PaperProps={{ sx: { bgcolor: '#120b24', color: 'white', borderRadius: 4 } }}>
        <DialogTitle sx={{ fontWeight: 'bold' }}>Program Store NFC Card</DialogTitle>
        <DialogContent>
          {nfcWriteStatus === 'writing' && (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 3, textAlign: 'center' }}>
              <CircularProgress color="secondary" sx={{ mb: 2 }} />
              <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 1 }}>
                Awaiting NFC Card Tap...
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.7 }}>
                Hold the back of your smartphone directly against your blank physical NFC card.
              </Typography>
            </Box>
          )}

          {nfcWriteStatus === 'success' && (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 3, textAlign: 'center' }}>
              <Typography variant="h6" color="success.main" sx={{ fontWeight: 'bold', mb: 1 }}>
                NFC Card Programmed!
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.7 }}>
                The physical NFC tag has been programmed successfully with this store's digital link hub.
              </Typography>
            </Box>
          )}

          {nfcWriteStatus === 'error' && (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 2 }}>
              <Alert severity="error" variant="outlined" sx={{ bgcolor: 'rgba(211, 47, 47, 0.1)', color: '#ff8a80', borderColor: 'error.main', mb: 2 }}>
                NFC Card Programming Failed
              </Alert>
              <Typography variant="body2" sx={{ opacity: 0.8, textAlign: 'center', wordBreak: 'break-word', fontSize: '0.85rem' }}>
                {nfcErrorDetails}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNfcWriteOpen(false)} color="inherit" sx={{ fontWeight: 'bold' }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar notification */}
      <Snackbar open={toastOpen} autoHideDuration={3000} onClose={() => setToastOpen(false)}>
        <Alert onClose={() => setToastOpen(false)} severity={toastSeverity} sx={{ width: '100%' }}>
          {toastMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default NfcSharePage;
